import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, IconButton, LinearProgress, Tooltip } from "@mui/material";
import { DataGrid, GridColDef, GridColumnHeaderParams } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";

import { campaignColumns } from "./cols";
import { Campaign } from "../../../models/Campaign";
import CustomPagination from "../../atoms/CustomPagination";
import EmptyState from "../../atoms/EmptyState";

interface CampaignDataGridProps {
  data: Campaign;
  loading?: boolean;
  onPageChange: (page: number) => void;
}

const STEP = 200;

const CampaignDataGrid = ({
  data,
  loading,
  onPageChange,
}: CampaignDataGridProps) => {
  const [cols, setCols] = useState<GridColDef<any>[]>([]);
  const [campaignNameToggled, setCampaignNameToggled] = useState(false);
  const [campaignNameBaseWidth, setCampaignNameBaseWidth] = useState<number | null>(null);

  useEffect(() => {
    const withHeaderControls = campaignColumns.map((col) => {
      if (col.field !== "campaignName") return col;

      const base = col.width ?? 160;
      if (campaignNameBaseWidth === null) setCampaignNameBaseWidth(base);

      const origRenderHeader = col.renderHeader;
      const enhanced: GridColDef<any> = {
        ...col,
        width: campaignNameToggled ? base + STEP : base,
        renderHeader: (params: GridColumnHeaderParams<any>) => {
          const headerContent = origRenderHeader
            ? origRenderHeader(params)
            : <span>{col.headerName ?? col.field}</span>;

          const toggleWidth = () => {
            setCampaignNameToggled((prev) => !prev);
            setCols((prev) =>
              prev.map((c) =>
                c.field === "campaignName"
                  ? {
                      ...c,
                      width: (campaignNameBaseWidth ?? base) + (campaignNameToggled ? 0 : STEP),
                    }
                  : c
              )
            );
          };

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%" }}>
              <Box sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {headerContent}
              </Box>
              <Tooltip title={campaignNameToggled ? "Collapse" : "Expand"}>
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleWidth();
                  }}
                  aria-label={campaignNameToggled ? "Collapse" : "Expand"}
                  sx={{ ml: "auto" }}
                >
                  {campaignNameToggled ? (
                    <ChevronLeftIcon fontSize="small" />
                  ) : (
                    <ChevronRightIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      };

      return enhanced;
    });

    setCols(withHeaderControls);
  }, [campaignColumns, campaignNameToggled, campaignNameBaseWidth]);

  return (
    <Box sx={{ height: 660, width: "100%", overflowX: "auto" }}>
      <Box sx={{ minWidth: 1090 }}>
        <DataGrid
          loading={loading}
          data-testid="campaign-table"
          autoHeight={false}
          sx={{
            minHeight: 660,
            fontFamily: "Novera,sans-serif",
            fontSize: 14,
            lineHeight: "20px",
            fontWeight: 400,
            "& .MuiDataGrid-footerContainer": {
              justifyContent: "center",
            },
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "primary.light",
            },
            "& .MuiDataGrid-row": {
              backgroundColor: "mono.white",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#E77825",
              color: "#FFF",
              fontWeight: "800",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 700,
              fontSize: "16px",
            },
            "& .MuiDataGrid-columnHeaderTitleContainer": {
              whiteSpace: "unset",
            },
            "& .MuiDataGrid-sortIcon": {
              color: "#E87722",
            },
          }}
          // onCellClick={data => {}}
          rows={data?.campaignDetails || []}
          columns={cols}
          getRowId={(row: any) => row.campaignID}
          slots={{
            pagination: CustomPagination,
            loadingOverlay: LinearProgress,
            noRowsOverlay: EmptyState,
          }}
          slotProps={{
            pagination: {
              count: data?.pagination?.totalPages,
              page: (data?.pagination?.currentPage ?? 1) - 1,
              onPageChange: (_, page) => {
                onPageChange(page + 1);
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default CampaignDataGrid;
